import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  buildAutoSignMailRoutingContext,
  prefetchAutoSignMailRoutingData,
  tryStartEligibleAutoAstSignMailHandoff,
} from './startAutoSignMailFlowHelpers';
import { getDeafUserInformationRequest } from './signMailThunkHelpers';
import { startSignMailSessionAndPrepareGreeting } from './thunks';

import { sendAnalyticsInfo } from 'features/analytics/helpers';
import type { AppDispatch, RootState } from 'features/app/store/store';
import {
  callIdSelector,
  callIdMatchesCurrentCall,
} from 'features/call/call-base/store';
import type { VideoMessageData } from 'features/signmail/interfaces';

/**
 * Starts the legacy local SignMail path after AST routing has been ruled out.
 *
 * When preloaded greeting data is available it is forwarded to avoid a second
 * Core lookup. If not, the downstream thunk performs its normal fetch.
 */
const startLegacySignMailFlow = (
  dispatch: AppDispatch,
  callId: number,
  preloadedVideoMsgData?: VideoMessageData
) =>
  dispatch(
    startSignMailSessionAndPrepareGreeting(
      preloadedVideoMsgData ? { callId, preloadedVideoMsgData } : { callId }
    )
  );

/**
 * Orchestrates the auto SignMail entry flow.
 *
 * Flow order:
 * 1. Read the local routing context from Redux.
 * 2. Prefetch Core data needed for both AST routing and local SignMail.
 * 3. Try the AST handoff when the prefetch results and local state allow it.
 * 4. Fall back to the standard SignMail greeting flow when AST is skipped,
 *    ineligible, or fails during handoff startup.
 *
 * This thunk keeps the branching policy in one place while delegating the
 * routing details to helper functions.
 */
export const startAutoSignMailFlow = createAsyncThunk(
  'signMail/startAutoSignMailFlow',
  async (_, { dispatch, getState }) => {
    const initialState = getState() as RootState;
    const appDispatch = dispatch as AppDispatch;
    const routingContext = buildAutoSignMailRoutingContext(initialState);
    const isCurrentCall = () =>
      callIdMatchesCurrentCall(getState() as RootState, routingContext.callId);
    const logCanceledFlow = () => {
      const currentCallId = callIdSelector(getState() as RootState);
      appDispatch(
        sendAnalyticsInfo({
          Method: 'startAutoSignMailFlow',
          message:
            'Auto SignMail flow canceled because this call is no longer active.',
          capturedCallId: routingContext.callId,
          currentCallId,
          LogGroup: 'SignMail',
        })
      );
    };

    if (!isCurrentCall()) {
      logCanceledFlow();
      return;
    }

    const prefetchResult = await prefetchAutoSignMailRoutingData(
      initialState,
      appDispatch,
      () => getState() as RootState,
      getDeafUserInformationRequest
    );

    if (!isCurrentCall()) {
      logCanceledFlow();
      return;
    }

    const astHandoffStarted = await tryStartEligibleAutoAstSignMailHandoff({
      dispatch: appDispatch,
      getState: () => getState() as RootState,
      routingContext,
      astSignMailMode: prefetchResult.astSignMailMode,
      isMailboxFull: prefetchResult.isMailboxFull,
    });

    if (astHandoffStarted) {
      return;
    }

    if (!isCurrentCall()) {
      logCanceledFlow();
      return;
    }

    await startLegacySignMailFlow(
      appDispatch,
      routingContext.callId,
      prefetchResult.preloadedVideoMsgData
    );
  }
);
